import { CommonMediaAttributes, Media } from "@/types/strapi";
import { IncomingHttpHeaders } from "node:http";
import { debugError } from "./logs";
import type { Site, USPAttributesGroup } from "@/types/sites";
import type { LandingPageTemplateResponse } from "@/types/landing";
import type { OfferPageTemplateResponse } from "@/types/offers";
import { isRunningOnLocalhost, isRunningOnVercel, isServerSide } from "./ssr";
import { isPrefetch } from "@/lib/prefetch";

export const replaceMerchantLogos = (htmlString: string): string => {
    // Replace all forms of merchant logos with simplified img tags
    return htmlString
        .replace(
            /<img\s+[^>]*\${merchantLogo}[^>]*>/g,
            '<img src="#merchantLogo">'
        )
        .replace(
            /<img\s+[^>]*\${merchantLogoSecondary}[^>]*>/g,
            '<img src="#merchantLogoSecondary">'
        )
        .replace(/\${merchantLogo}/g, '<img src="#merchantLogo">')
        .replace(
            /\${merchantLogoSecondary}/g,
            '<img src="#merchantLogoSecondary">'
        )
        .replace(
            /<div[^>]*class="frontend-merchantLogo"[^>]*>.*?<\/div>/g,
            '<img src="#merchantLogo">'
        )
        .replace(
            /<div[^>]*class="frontend-merchantLogoSecondary"[^>]*>.*?<\/div>/g,
            '<img src="#merchantLogoSecondary">'
        );
};
export const replacingStringTemplate = (
    str: string,
    obj: { [key: string]: string | number | undefined }
) => {
    // Convert score to stars using specific font glyphs
    const generateStars = (score?: string | number) => {
        const FULL_STAR = "\uf005"; // FontAwesome solid star
        const HALF_STAR = "\uf089"; // FontAwesome half star

        const numericScore = Number(score) || 8; // Default to 4 stars (8/10)
        const fullStars = Math.floor(numericScore / 2);
        const hasHalfStar = numericScore % 2 >= 1;

        const stars =
            FULL_STAR.repeat(fullStars) + (hasHalfStar ? HALF_STAR : "");
        const bgStars = FULL_STAR.repeat(5);
        return `<div class="stars-container"><div class="stars">${stars}</div><div class="stars-bg">${bgStars}</div></div>`;
    };
    const starsReplacedStr = str.replace(/\${stars}/g, () => {
        return generateStars(obj.scoreOutOf10);
    });
    // Logos are replaced with next/image later
    const logoReplacedStr = replaceMerchantLogos(starsReplacedStr);
    return logoReplacedStr.replace(/\${(?!\d)\w*}/g, (match) => {
        const key = match.slice(2, -1);
        return obj[key]?.toString() ?? "";
    });
};

export const generateTopics = (
    host: string,
    additionalTopics: string[],
    cakeTopic: string
): string[] => {
    let baseTopics = [cakeTopic];

    const hosts_to_send_lead = [
        "qih-dev-1.ancoristest",
        "qih-staging-1.ancoristest",
        "qih-uat-1.ancoristest",
        "strikewild",
        "localhost",
    ];

    if (hosts_to_send_lead.some((lead_host) => host?.includes(lead_host))) {
        baseTopics = [...baseTopics, ...additionalTopics];
    }

    return baseTopics;
};

export const getImageUrl = (path = ""): string => {
    // When running locally the image url returned by the API is just a path to the image, so it needs to be
    // prepended with the host
    // When running in App Engine the url is a cloud storage link and does not need any modification
    if (path?.startsWith("https")) {
        return path;
    } else if (path) {
        return `${process.env.NEXT_PUBLIC_API_URL}${path}`;
    } else {
        return "";
    }
};

export const getAllMedias = (
    medias: Media | Media[] | null
): CommonMediaAttributes[] => {
    // Media properties in Strapi can be single types or multi types.
    // If it's a multi type we may have an array of images, and each one will have its own variations.
    // This function takes a media field and will return an array containing ALL variations
    if (medias) {
        medias = Array.isArray(medias) ? medias : [medias];
        return medias
            .map((data: Media): CommonMediaAttributes[] => {
                return [
                    // Webp format first to prioritise it.
                    data?.attributes?.formats?.xlarge_webp,
                    data?.attributes?.formats?.large_webp,
                    data?.attributes?.formats?.medium_webp,
                    data?.attributes?.formats?.small_webp,
                    data?.attributes?.formats?.logo_webp,
                    // Where the image is an ICO, there are no formats.
                    data?.attributes,
                    data?.attributes?.formats?.xlarge,
                    data?.attributes?.formats?.large,
                    data?.attributes?.formats?.medium,
                    data?.attributes?.formats?.small,
                    data?.attributes?.formats?.logo,
                ].filter(
                    (
                        media: CommonMediaAttributes | undefined
                    ): media is CommonMediaAttributes => !!media
                );
            })
            .flat(1);
    } else {
        return [];
    }
};

export const closestNumberInList = (goal: number, list: number[]): number => {
    const onlyLarger = list.filter((num) => num >= goal);

    const getNearest = (items: number[]) =>
        items.reduce((previousValue: number, currentValue: number): number => {
            return Math.abs(currentValue - goal) <
                Math.abs(previousValue - goal)
                ? currentValue
                : previousValue;
        }, onlyLarger[0]);

    // Get the smallest item above target, if none return largest.
    return onlyLarger.length > 0 ? getNearest(onlyLarger) : Math.max(...list);
};

export const isSecure = (headers: IncomingHttpHeaders | undefined): boolean => {
    const isLocalhost = headers?.host?.includes("localhost");
    const isHttp = headers && headers["x-forwarded-proto"] == "http";
    // If localhost, or the xforwardedProtocol is http, then we are not secure.
    // we currently have `secure: always` set in the app.yaml, just assume if we
    // are running on appengine then we are secure.
    // Return false if we're on localhost, using http, or not in production
    return process.env.NODE_ENV === "production" && !isLocalhost && !isHttp;
};

export const percentToHexadecimal = (percent: number) => {
    let percentInRange = percent;
    if (percentInRange > 100) {
        percentInRange = 100;
    } else if (percentInRange < 0) {
        percentInRange = 0;
    }
    const decimal = percentInRange / 100;
    const eightBit = Math.round(decimal * 255);
    return eightBit.toString(16);
};

export const getRandomFloatInRange = (min: number, max: number): number => {
    // https://www.w3schools.com/js/js_random.asp
    // returns a random float between min (included) and max (excluded)
    // using the +1 to max to make it inclusive doesn't work for floats like it does for ints but in
    // our current use case this doesn't matter we can use an exclusive max just fine for now.
    return Math.random() * (max - min) + min;
};

export const getRandomIntInRange = (
    min: number,
    max: number,
    maxIncluded = true
): number => {
    // https://www.w3schools.com/js/js_random.asp
    // returns a random integer between min (included) and max (optional, included by default)
    return (
        Math.floor(Math.random() * (max - min + (maxIncluded ? 1 : 0))) + min
    );
};

/*
 * Can we access Cloud services, e.g. Datastore, Pub/Sub etc.?
 * To do so ADCs need to be configured, and we must not be running in the browser
 *
 * Args:
 *     context: the context from GetServerSideProps
 *     serviceName: the name of the service, used for logging (e.g. "Datastore", "Pub/Sub")
 * Returns:
 *     bool
 * Raises:
 *     Error, in the following circumstances:
 *         - if ADCs are not configured AND we're running in the cloud.
 *         - if we try to run this code in the browser
 */
export const canAccessGCP = (
    host: string | undefined,
    serviceName: string
): boolean => {
    if (process.env.GOOGLE_APPLICATION_CREDENTIALS == "") {
        // https://cloud.google.com/docs/authentication/client-libraries#node.js

        if (isRunningOnLocalhost(host) || isRunningOnVercel()) {
            // Local dev should be able to operate without writing to a GCP service
            debugError(
                `Cannot write to ${serviceName}. GOOGLE_APPLICATION_CREDENTIALS must be configured first`
            );
            return false;
        } else {
            throw new Error(
                "GCP Application Default Credentials (ADCs) must be configured"
            );
        }
    }
    if (!isServerSide()) {
        throw new Error(
            `Insecure ${serviceName} request. ${serviceName} can only be accessed by the server`
        );
    }
    return true;
};

export const buttonToggle = (
    formTarget: HTMLFormElement,
    disabled: boolean,
    timed = true
) => {
    const buttons = formTarget.querySelectorAll("button");
    buttons.forEach((button) => {
        button.disabled = disabled;
        button.style.cursor = disabled ? "not-allowed" : "pointer";
    });

    if (disabled && timed) {
        setTimeout(() => {
            buttonToggle(formTarget, false);
        }, 10000);
    }
};

// This function is used to toggle the required attribute on email fields as we
// need to make sure that the email field is required when the form is submitted
// but not required when using social sign in.
export const emailRequiredToggle = (
    formTarget: HTMLFormElement,
    required: boolean
) => {
    const emailFields = formTarget.querySelectorAll("input[type='email']");
    emailFields.forEach((emailField: Element) => {
        (emailField as HTMLInputElement).required = required;
    });
};

export const LEAD_FORM_SUBMITTED_EVENT_NAME = "lead-form-submitted";

export const logNewGoogleAnalyticsEvent = (
    eventName: string,
    customLabels?: Record<string, string>,
    landingPage?: string
): void => {
    if (isPrefetch()) {
        return;
    }

    // Datalayer is only added if Google Tag Manager Container ID is set in the CMS
    window.dataLayer?.push({
        event: eventName,
        landingPage: landingPage ?? window.location.href,
        ...customLabels,
    });
};

export const getPageUSPAttributes = (
    site: Site | null,
    landingPage: LandingPageTemplateResponse | null,
    offerPage: OfferPageTemplateResponse | null
): USPAttributesGroup | null => {
    const defaultUSPAttributes = site?.attributes?.attribution_group?.data;

    return (
        landingPage?.data?.attributes?.attribution_group?.data ??
        offerPage?.data?.attributes?.attribution_group?.data ??
        defaultUSPAttributes ??
        null
    );
};

export async function hashStringFrontend(
    string: string | undefined
): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(string);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

export const sanitizeForJSON = <T>(obj: any): T | null => {
    if (obj === undefined) return null;
    if (obj === null || typeof obj !== "object") return obj;

    if (Array.isArray(obj)) {
        return obj.map((item) => sanitizeForJSON(item)) as T;
    }

    return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [key, sanitizeForJSON(value)])
    ) as T;
};
